import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../../services/api';

// The org's chat fragment on its own, for the "Organization-wide" scope. It is the same fragment
// the pathway views show as "Chat fragment (one per org, all pathways)", but here it needs no
// domain or pathway: it reaches every question in the org that no domain bundle serves, so an
// org whose datasets carry no domain can still be given chat context. Publishing it stages the
// fragment directly; there is no bundle compile and no eval.
function OrgWideChatPanel({ orgId, onBusyChange }) {
  const [fragment, setFragment] = useState('');
  const [version, setVersion] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const data = await apiService.getOrgWideChat(orgId);
      setFragment(data.chat_fragment || '');
      setVersion(data.version || null);
      setDirty(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [orgId]);

  useEffect(() => {
    setStatus('');
    load();
  }, [load]);

  useEffect(() => {
    if (onBusyChange) onBusyChange(busy);
  }, [busy, onBusyChange]);

  useEffect(() => () => onBusyChange && onBusyChange(false), [onBusyChange]);

  const handlePublish = async () => {
    setBusy(true);
    setError('');
    setStatus('');
    try {
      await apiService.saveContextLayers(orgId, [], fragment, null);
      const result = await apiService.publishContextChatUpdate(null, null, orgId);
      setStatus(`Published org-wide chat: ${result.from_version || '(none)'} -> ${result.to_version || '(none)'}`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ca-section">
      <h3>Organization-wide chat (all questions)</h3>
      <p className="ca-note">
        Applies to every chat, Insights and report question in this organization that no domain context
        covers, including datasets with no domain. Published version: {version || 'none yet'}.
      </p>
      {error && <div className="ca-error">{error}</div>}
      <textarea
        className="ca-chat-fragment"
        rows={8}
        value={fragment}
        onChange={(e) => { setFragment(e.target.value); setDirty(true); }}
        disabled={busy}
      />
      <div className="ca-actions">
        <button onClick={handlePublish} disabled={busy || !dirty}>
          Publish org-wide chat
        </button>
        {status && <div className="ca-status">{status}</div>}
      </div>
    </div>
  );
}

export default OrgWideChatPanel;
